import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BuildType } from 'mbs-ui-shared@models';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { Build, BuildsInfo, getNewBuildSection } from 'mbs-ui-shared@models/build';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonResponseType, RequestBuildOptionsType } from './build-service.types';

@Injectable({
  providedIn: 'root'
})
export class BuildService {
  private http = inject(HttpClient);

  private readonly controllerPath = '/api/build';

  getAll(): Observable<BuildsInfo> {
    return this.http.get<BuildsInfo>(`${this.controllerPath}/info`, { headers: { 'x-front-queue': 'true' } });
  }

  getDownloadInfo(): Observable<{ builds: Build[] }> {
    return this.http.get<{ builds: Build[] }>(`${this.controllerPath}/downloads-info`, { headers: { 'x-front-queue': 'true' } });
  }

  requestBuild({ buildsTypeList: builds, isBuildDestinationSandbox }: RequestBuildOptionsType): Observable<CommonResponseType> {
    return this.http.post<CommonResponseType>(`${this.controllerPath}/request`, {
      builds,
      isBuildDestinationSandbox
    });
  }

  removeBuild({ mbsBuildType: type }: Build, isSandboxBuild: boolean): Observable<CommonResponseType> {
    return this.http.post<CommonResponseType>(`${this.controllerPath}/remove`, {
      type,
      isSandboxBuild
    });
  }

  makePublic({ mbsBuildType: type }: Build): Observable<CommonResponseType> {
    return this.http.post<CommonResponseType>(`${this.controllerPath}/make-public`, { type });
  }

  updateDownloadSettings(
    settings: Partial<{ isSandboxEnabled: boolean; isAutoUpdateEnabled: boolean; isAutoGenerateEnabled: boolean }>
  ): Observable<CommonResponseType> {
    return this.http.post<CommonResponseType>(`${this.controllerPath}/downloads/update-settings`, settings);
  }

  isSandBoxAvailable(): Observable<boolean> {
    return this.getAll().pipe(map((data) => data?.sandBoxAvailable ?? false));
  }

  getActiveBuildRequest(): Observable<{ requestedBuild: BuildType }[]> {
    return this.http.get<{ requestedBuild: BuildType }[]>(`${this.controllerPath}/active-build-request`, {
      headers: { 'x-front-queue': 'true' }
    });
  }

  getNewVersions(
    builds: Build[],
    isSandBoxAvailable: boolean
  ): {
    backupVersions: string[];
    rmmVersions: string[];
    raVersions: string[];
  } {
    const getVersions = (builds: Build[], isSandBoxAvailable: boolean): string[] => {
      return builds
        .filter((build) => getNewBuildSection(build, isSandBoxAvailable)?.isNewerVersionAvailable)
        .sort((first, second) => first.mbsBuildType - second.mbsBuildType)
        .map((build) => getNewBuildSection(build, isSandBoxAvailable)?.availableVersion);
    };

    const backupAgentsNewVersions = getVersions(
      builds.filter((build) => build.applicationId === AgentType.Backup),
      isSandBoxAvailable
    );
    const rmmAgentsVersions = getVersions(
      builds.filter((build) => build.applicationId === AgentType.RMM),
      isSandBoxAvailable
    );

    const raAgentsVersions = getVersions(
      builds.filter((build) => build.applicationId === AgentType.RA),
      false
    );

    return {
      backupVersions: backupAgentsNewVersions,
      rmmVersions: rmmAgentsVersions,
      raVersions: raAgentsVersions
    };
  }

  generateHashCode(versions: string[]): string {
    const hash = versions
      .join('')
      .split('')
      .reduce((acc, char) => {
        acc = (acc << 5) - acc + char.charCodeAt(0);
        return acc & acc;
      }, 0);

    return String(hash);
  }
}
